import React, { useRef } from 'react';
import styles from '@components/catalog/sort/sort.module.scss';
import { useTranslation } from 'next-i18next';
import { useAriaKeyboardNavigation } from '@hooks/a11y/use-aria-keyboard-navigation';

interface SortOptionsListProps<T extends string | number> {
  selectedValue: T;
  handleChange: (value: T) => void;
  options: {
    text: string;
    value: T;
  }[];
}

const SortOptionsList = <T extends string | number>({
  options,
  handleChange,
  selectedValue,
}: SortOptionsListProps<T>) => {
  const { t } = useTranslation();
  const optionsRef = useRef<(HTMLDivElement | null)[]>([]);

  // Find selected index
  const selectedIndex = options.findIndex((opt) => opt.value === selectedValue);

  // Use keyboard navigation hook with listbox pattern (manual activation)
  const { containerProps, getItemProps } = useAriaKeyboardNavigation({
    pattern: 'listbox',
    direction: 'vertical',
    itemsRef: optionsRef,
    totalItems: options.length,
    selectedIndex: selectedIndex >= 0 ? selectedIndex : 0,
    onSelect: (index) => handleChange(options[index].value),
    autoActivate: false, // Arrows only move focus, Enter/Space activates
    loop: true,
    idPrefix: 'sort-option',
  });

  return (
    <div
      {...containerProps}
      aria-label={t('CATALOG.FILTERS.SORT.SORT_BY')}
    >
      {options.map((option, index) => {
        const isSelected = option.value === selectedValue;
        const itemProps = getItemProps(index);

        return (
          <div
            key={option.value}
            {...itemProps}
            className={styles['radio-option']}
            onClick={() => handleChange(option.value)}
          >
            <input
              className={styles['radio-option__input']}
              type="radio"
              value={option.value}
              checked={isSelected}
              tabIndex={-1}
              readOnly
              data-cy={`catalog-sort-popup-option-${option.value}`}
              aria-label={`${t('CATALOG.FILTERS.SORT.SORT_BY')} - ${option.text}`}
            />
            <span aria-hidden="true">{option.text}</span>
          </div>
        );
      })}
    </div>
  );
};

export default SortOptionsList;
